import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import {
  allYearOptions,
  filterAndSort,
  getCategoryMeta,
  getMoviesByCategory,
  regionOptions,
  tagOptions,
  type FilterState,
  type SortKey,
} from "@/utils/movie";

const PAGE_SIZE = 12;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "update", label: "按更新时间" },
  { key: "heat", label: "按热度" },
  { key: "rating", label: "按评分" },
];

const filterGroups = [
  { key: "region", label: "地区", options: regionOptions },
  { key: "year", label: "年份", options: allYearOptions },
  { key: "tag", label: "标签", options: tagOptions },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const meta = getCategoryMeta(type);

  const [filters, setFilters] = useState<FilterState>({
    region: "全部",
    year: "全部",
    tag: "全部",
  });
  const [sort, setSort] = useState<SortKey>("update");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters({ region: "全部", year: "全部", tag: "全部" });
    setSort("update");
    setPage(1);
    window.scrollTo({ top: 0 });
  }, [type]);

  usePageMeta({
    title: meta ? meta.title : "影视分类 - 蓝莓视频",
    description: meta
      ? meta.description
      : "浏览蓝莓视频丰富的影视分类资源，涵盖电影、电视剧等高清内容。",
    keywords: `蓝莓视频,${meta ? meta.label : "影视分类"},高清影视资源,在线视频`,
  });

  const baseList = useMemo(() => getMoviesByCategory(type), [type]);
  const filtered = useMemo(
    () => filterAndSort(baseList, filters, sort),
    [baseList, filters, sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleSort = (key: SortKey) => {
    setSort(key);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ region: "全部", year: "全部", tag: "全部" });
    setSort("update");
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16">
        <div className="px-4 md:px-8 py-8 md:py-10 bg-background-100 border-b border-background-200/70">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{meta ? meta.label : "分类"}</span>
          </nav>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-3">
            {meta ? meta.label : "影视分类"}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            {meta
              ? meta.description
              : "浏览蓝莓视频丰富的影视分类资源，涵盖电影、电视剧等高清内容。"}
          </p>
        </div>

        <div className="px-4 md:px-8 py-8 md:py-10">
          <FilterBar groups={filterGroups} values={filters} onChange={handleFilterChange} />

          <div className="flex items-center justify-between gap-4 flex-wrap mt-6 mb-6">
            <p className="text-sm text-foreground-500">
              共 <span className="text-accent-500 font-medium">{filtered.length}</span> 部作品
            </p>
            <div className="p-1 rounded-full bg-background-100 border border-background-200 flex items-center gap-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => handleSort(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    sort === opt.key
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h3 className="text-lg font-medium text-foreground-900 mb-2">暂无匹配的影视作品</h3>
              <p className="text-sm text-foreground-500 mb-6">
                换个筛选条件试试，或重置后浏览全部内容
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          )}

          <Pagination current={page} total={totalPages} onChange={setPage} />
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}